import React, { useState, useEffect, useImperativeHandle } from 'react';
import { Paperclip, Save, Lock, FileText as FileIcon, Image as ImageIcon, Edit2 } from 'lucide-react';
import { getImageUrl } from '../utils/imageUrl';

const URL_OR_PHONE_REGEX = /(https?:\/\/[^\s]+|\+[\d\s()-]{6,17}\d|\b\d{11}\b)/g;
const TRAILING_PUNCT_REGEX = /[.,;:!?)\]}»"']+$/;

function linkify(text) {
  return text.split(URL_OR_PHONE_REGEX).map((part, i) => {
    if (i % 2 === 0) return part;
    if (part.startsWith('+')) {
      const digits = part.replace(/\D/g, '');
      return (
        <a key={i} href={`https://wa.me/${digits}`} target="_blank" rel="noreferrer noopener" className="text-green-400 hover:text-green-300 underline">
          {part}
        </a>
      );
    }
    if (/^\d{11}$/.test(part)) {
      if (part[2] === '9') {
        return (
          <a key={i} href={`https://wa.me/55${part}`} target="_blank" rel="noreferrer noopener" className="text-green-400 hover:text-green-300 underline">
            {part}
          </a>
        );
      }
      return part;
    }
    const trailMatch = part.match(TRAILING_PUNCT_REGEX);
    const trail = trailMatch ? trailMatch[0] : '';
    const url = trail ? part.slice(0, -trail.length) : part;
    return (
      <React.Fragment key={i}>
        <a href={url} target="_blank" rel="noreferrer noopener" className="text-[#1E3A8A] hover:text-[#172554] underline break-all">{url}</a>
        {trail}
      </React.Fragment>
    );
  });
}

const WEEKDAYS = ['Domingo', 'Segunda-feira', 'Terça-feira', 'Quarta-feira', 'Quinta-feira', 'Sexta-feira', 'Sábado'];

export function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function todayStr() {
  return new Date().toISOString().slice(0, 10);
}

function formatDateLabel(dataRef) {
  const [y, m, d] = dataRef.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  return `${String(d).padStart(2, '0')}/${String(m).padStart(2, '0')}/${y} — ${WEEKDAYS[dt.getDay()]}`;
}

function statusStyle(dataRef, conteudo) {
  const today = todayStr();
  const preenchido = !!(conteudo && conteudo.trim());
  if (dataRef > today) return { wrapper: 'bg-[#EEF2FF] border-gray-200 opacity-60', dot: 'bg-zinc-700', label: 'Futuro — bloqueado' };
  if (dataRef === today) return preenchido
    ? { wrapper: 'bg-green-50 border-green-200', dot: 'bg-green-500', label: 'Preenchido hoje' }
    : { wrapper: 'bg-blue-50 border-[#1E3A8A]/30', dot: 'bg-[#1E3A8A]', label: 'Pendente hoje — urgente' };
  return preenchido
    ? { wrapper: 'bg-white border-gray-200', dot: 'bg-zinc-400', label: 'Preenchido' }
    : { wrapper: 'bg-red-50 border-red-200', dot: 'bg-red-500', label: 'Atrasado — vazio' };
}

/**
 * Card de um dia do diário de atividade. Auto-contido: gerencia seu próprio
 * estado de edição/upload e só delega a chamada de rede pro `onSave`/`onUpload`.
 * `ref` expõe `save()` e `onDirtyChange` reporta o estado de `dirty` pro pai.
 */
const AgendaDiaryCard = React.forwardRef(function AgendaDiaryCard({ entry, editable, onSave, onUpload, onDirtyChange, templateToInsert }, ref) {
  const [conteudo, setConteudo] = useState(entry.conteudo || '');
  const [isEditing, setIsEditing] = useState(!entry.conteudo);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const style = statusStyle(entry.data_referencia, entry.conteudo);
  const isFuture = entry.data_referencia > todayStr();

  useEffect(() => { onDirtyChange?.(dirty); }, [dirty, onDirtyChange]);

  // Preenchido pelo botão de progresso (%) na tela de detalhe — substitui o
  // rascunho do dia pelo template "Hoje executei X%..." e reabre a edição.
  useEffect(() => {
    if (!templateToInsert) return;
    setConteudo(templateToInsert.text);
    setIsEditing(true);
    setDirty(true);
  }, [templateToInsert]);

  const handleSaveClick = async () => {
    setSaving(true);
    try {
      await onSave(entry.data_referencia, conteudo);
      setDirty(false);
      setIsEditing(false);
    } finally {
      setSaving(false);
    }
  };

  useImperativeHandle(ref, () => ({ save: handleSaveClick }));

  const startEditing = () => {
    setConteudo(entry.conteudo || '');
    setIsEditing(true);
  };

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    setUploading(true);
    try {
      await onUpload(entry.data_referencia, files);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className={`border rounded-2xl p-4 space-y-3 ${style.wrapper}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className={`w-2.5 h-2.5 rounded-full ${style.dot}`} />
          <span className="text-sm font-medium text-gray-900 font-manrope">{formatDateLabel(entry.data_referencia)}</span>
        </div>
        <span className="text-xs text-gray-500 font-manrope">{style.label}</span>
      </div>

      {isFuture ? (
        <p className="text-xs text-gray-500 flex items-center gap-2 font-manrope"><Lock size={12} /> Disponível após 00:00 do dia</p>
      ) : editable && isEditing ? (
        <>
          <textarea
            className="w-full bg-white border border-gray-300 rounded-xl px-3 py-2 text-sm text-black placeholder-gray-400 focus:outline-none focus:border-[#1E3A8A] min-h-[80px] resize-y font-manrope"
            value={conteudo}
            maxLength={5000}
            onChange={e => { setConteudo(e.target.value); setDirty(true); }}
            placeholder="Descreva o andamento do dia..."
          />
          <div className="flex items-center justify-between gap-2">
            <label className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-900 cursor-pointer font-manrope">
              <Paperclip size={13} />
              {uploading ? 'Enviando...' : 'Anexar arquivo'}
              <input
                type="file" accept="image/jpeg,image/png,application/pdf" multiple
                className="hidden" onChange={handleFiles} disabled={uploading}
              />
            </label>
            <button
              onClick={handleSaveClick}
              disabled={!dirty || saving}
              className="flex items-center gap-1.5 bg-[#1E3A8A] hover:bg-[#172554] disabled:opacity-40 text-white text-xs font-semibold px-3 py-1.5 rounded-full transition-colors font-manrope"
            >
              <Save size={13} /> {saving ? 'Salvando...' : 'Salvar registro'}
            </button>
          </div>
        </>
      ) : (
        <div className="space-y-2">
          <p className="text-sm text-gray-500 whitespace-pre-wrap font-manrope">
            {entry.conteudo ? linkify(entry.conteudo) : <span className="text-zinc-600 italic">Sem registro</span>}
          </p>
          {editable && (
            <button
              onClick={startEditing}
              className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-900 transition-colors font-manrope"
            >
              <Edit2 size={12} /> Editar
            </button>
          )}
        </div>
      )}

      {entry.anexos?.length > 0 && (
        <div className="flex flex-wrap gap-2 pt-1">
          {entry.anexos.map((a, i) => (
            <a
              key={i} href={getImageUrl(a.path)} target="_blank" rel="noreferrer"
              className="flex items-center gap-1.5 bg-[#EEF2FF] border border-gray-300 rounded-lg px-2 py-1 text-xs text-gray-500 hover:text-gray-900"
            >
              {a.tipo_mime === 'application/pdf' ? <FileIcon size={12} /> : <ImageIcon size={12} />}
              <span className="truncate max-w-[120px]">{a.nome_original}</span>
            </a>
          ))}
        </div>
      )}
    </div>
  );
});

export default AgendaDiaryCard;
